import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DragEndEvent } from "@dnd-kit/core";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import { Button } from "src/app/components/Button/Button";
import CustomInput from "src/app/components/CustomInput/CustomInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import NoResultsMessage from "src/app/components/NoResultsMessage/NoResultsMessage";
import { tableDataType } from "src/app/components/Table/Table";
import { VerticalDragAndDropList } from "src/app/components/VerticalDragAndDropList/VerticalDragAndDropList";
import { filterGlobalMetaFields } from "src/app/methods/getMetaFieldTypeOptions";
import { translateMessage } from "src/app/methods/translateMessage";
import { ReactComponent as PlusIcon } from "src/images/plus-white.svg";
import {
  updateGlobalProjectFieldRank,
  updateGlobalTaskFieldRank,
} from "src/redux";
import { RootState } from "src/redux/reducers";
import {
  GlobalProjectMetaField,
  GlobalTaskMetaField,
} from "src/redux/settings/settingsReducer";
import { generateRankString } from "src/utils/rankStrings";
import ManageFieldModal from "../../ManageFieldModal";
import DraggableGlobalFieldItem from "./components/DraggableGlobalFieldItem";
import GlobalFieldsSkeleton from "./components/GlobalFieldsSkeleton";
import { searchObjectsByName } from "./utils";

import "./GlobalFields.scss";

export type GlobalField = GlobalProjectMetaField | GlobalTaskMetaField;

interface GlobalFieldsProps {
  options: Array<GlobalField>;
  dataType?: tableDataType;
  selectedGlobalMetaFields?: Array<GlobalField>;
  setSelectedGlobalMetaFields?: Dispatch<SetStateAction<Array<GlobalField>>>;
  isLoading?: boolean;
}

function GlobalFields({
  options,
  dataType,
  selectedGlobalMetaFields,
  setSelectedGlobalMetaFields,
  isLoading,
}: GlobalFieldsProps) {
  const [value, setValue] = useState("");
  const [displayOptions, setDisplayOptions] = useState<Array<GlobalField>>([]);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);

  const {
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);

  const dispatch = useDispatch();
  const intl = useIntl();

  const filteredOptions = useMemo(
    () => filterGlobalMetaFields(options, dataType),
    [options, dataType],
  );

  const enableFieldManaging = dataType;

  const handleSelectGlobalMetaFields = (selectedField: GlobalField) => {
    if (!setSelectedGlobalMetaFields) return;
    setSelectedGlobalMetaFields((prev) => {
      const isFieldSelected = prev.find(
        (field) => field.uuid === selectedField.uuid,
      );

      if (isFieldSelected) {
        const updatedGlobalMetaFieldData = prev.filter(
          (field) => field.uuid !== selectedField.uuid,
        );
        return [...updatedGlobalMetaFieldData];
      }

      return [...prev, selectedField];
    });
  };

  const handleClick = (option: GlobalField) => {
    if (!option.existsInProject && !enableFieldManaging) {
      handleSelectGlobalMetaFields(option);
    }
  };

  useEffect(() => {
    if (!value) {
      setDisplayOptions(filteredOptions);
    } else {
      const searchResults = searchObjectsByName(value, filteredOptions);
      setDisplayOptions(searchResults);
    }
  }, [value, filteredOptions]);

  useEffect(() => {
    return () => {
      if (setSelectedGlobalMetaFields) {
        setSelectedGlobalMetaFields([]);
      }
    };
  }, [setSelectedGlobalMetaFields]);

  const updateRank = (uuid: string, newRank: string) => {
    switch (dataType) {
      case tableDataType.Task:
        dispatch(updateGlobalTaskFieldRank(activeWorkspaceUuid, uuid, newRank));
        break;

      case tableDataType.Project:
        dispatch(
          updateGlobalProjectFieldRank(activeWorkspaceUuid, uuid, newRank),
        );
        break;
    }
  };

  const sortedDisplayOptions = useMemo(
    () => [...displayOptions].sort((a, b) => a.rank.localeCompare(b.rank)),
    [displayOptions],
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sortedDisplayOptions.findIndex(
      (field) => field.uuid === active.id,
    );
    const newIndex = sortedDisplayOptions.findIndex(
      (field) => field.uuid === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const sourceField = sortedDisplayOptions[oldIndex];
    const fieldIndex = newIndex;

    let newRank = "";
    if (newIndex < oldIndex) {
      // move up
      newRank = generateRankString(
        sortedDisplayOptions[fieldIndex - 1]?.rank || "",
        sortedDisplayOptions[fieldIndex].rank,
      );
    } else {
      // move down
      newRank = generateRankString(
        sortedDisplayOptions[fieldIndex].rank,
        sortedDisplayOptions[fieldIndex + 1]?.rank || "",
      );
    }
    updateRank(sourceField.uuid, newRank);
  };

  return (
    <AnimatedDiv className="global-fields">
      <div className="global-fields__input-wrapper">
        <CustomInput
          type="text"
          placeholder={translateMessage({
            intl,
            id: "ws_search_for_predefined_column",
            defaultMessage: "Search for a predefined column",
          })}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value)
          }
        />
        {enableFieldManaging && (
          <Button
            size="large"
            variant="blue"
            onClick={() => setShowAddFieldModal(true)}
          >
            <PlusIcon />
            <IDHFormattedMessage id="ws_add_new" defaultMessage="Add new" />
          </Button>
        )}
      </div>
      <div className="global-fields__header">
        <span className="global-fields__header-column-name">
          <IDHFormattedMessage
            id="ws_column_name"
            defaultMessage="Column name"
          />
        </span>
        {enableFieldManaging && (
          <span className="global-fields__header-used">
            <IDHFormattedMessage id="ws_used" defaultMessage="Used" />
          </span>
        )}
        {dataType === tableDataType.Task && (
          <span className="global-fields__header-applicable-for">
            <IDHFormattedMessage
              id="ws_applicable_for"
              defaultMessage="Applicable for"
            />
          </span>
        )}
        <span className="global-fields__header-type">
          <IDHFormattedMessage id="ws_type" defaultMessage="Type" />
        </span>
        {enableFieldManaging && (
          <span className="global-fields__header-action">
            <IDHFormattedMessage id="ws_action" defaultMessage="Action" />
          </span>
        )}
      </div>
      {isLoading ? (
        <GlobalFieldsSkeleton
          dataType={dataType}
          enableFieldManaging={!!enableFieldManaging}
        />
      ) : (
        <VerticalDragAndDropList.DndContext
          onDragEnd={enableFieldManaging ? handleDragEnd : undefined}
        >
          <VerticalDragAndDropList.SortableContext
            items={sortedDisplayOptions.map((field) => field.uuid)}
          >
            <ul className="global-fields__list">
              {sortedDisplayOptions.length > 0 ? (
                sortedDisplayOptions.map((option) => {
                  const isFieldSelected = selectedGlobalMetaFields?.find(
                    (field) => field.uuid === option.uuid,
                  );
                  return (
                    <DraggableGlobalFieldItem
                      key={option.uuid}
                      option={option}
                      dataType={dataType}
                      isFieldSelected={!!isFieldSelected}
                      enableFieldManaging={!!enableFieldManaging}
                      handleClick={handleClick}
                    />
                  );
                })
              ) : (
                <NoResultsMessage />
              )}
            </ul>
          </VerticalDragAndDropList.SortableContext>
        </VerticalDragAndDropList.DndContext>
      )}

      {showAddFieldModal && enableFieldManaging && (
        <ManageFieldModal
          onClose={() => setShowAddFieldModal(false)}
          title={
            <IDHFormattedMessage id="ws_add_field" defaultMessage="Add field" />
          }
          inputLabelText={
            <IDHFormattedMessage id="ws_name" defaultMessage="Name" />
          }
          dataType={dataType}
          globalFieldMode
        />
      )}
    </AnimatedDiv>
  );
}

export default GlobalFields;
